import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, memo, useContext, useMemo, useState } from 'react';
import { UserRole } from '@/services/ws-permissions/src/main/resources/rest-api/index.ts';
import { PermissionType } from '@/js/libs/generated/permissions/types.ts';
import { AGENCY_ROLES, NEW_COMPANY_EXCLUDED_PERMISSIONS_SET, YALEO_SELF_BOOKING_USER_ROLES } from './constants.js';
import useTokenStorage from './hooks/useTokenStorage.js';
import { CombinedRole } from './types.js';
const AuthContext = createContext(null);
export const AuthProvider = memo(function AuthProvider({ children }) {
    const { getAuthToken } = useTokenStorage();
    const [token, setToken] = useState(getAuthToken);
    const [identity, setIdentity] = useState({
        user: undefined,
        loading: false,
        initialized: false,
    });
    const identityRoles = useMemo(() => {
        const user = identity.user;
        if (!user?.id) {
            return [PermissionType.ANONYMOUS];
        }
        const isAdmin = user.userRole === UserRole.ADMIN;
        const isAgencyRelatedRoles = isAdmin || AGENCY_ROLES.some((role) => user.permissions.includes(role));
        const isSelfBookingUser = user.permissions.some((permission) => permission === PermissionType.YALEO_SELF_BOOKING) &&
            user.permissions.every((permission) => YALEO_SELF_BOOKING_USER_ROLES.includes(permission));
        const isNewCompany = user.permissions.every((permission) => NEW_COMPANY_EXCLUDED_PERMISSIONS_SET.has(permission));
        return [
            user.userRole,
            ...user.permissions,
            ...(isAdmin && !user.permissions.includes(PermissionType.PUBLISHER) ? [PermissionType.PUBLISHER] : []),
            ...(isAgencyRelatedRoles ? [CombinedRole.AGENCY] : []),
            ...(isSelfBookingUser ? [CombinedRole.YALEO_SELF_BOOKING_USER] : []),
            ...(isNewCompany ? [CombinedRole.NEW_COMPANY] : []),
        ].filter((role) => !!role);
    }, [identity]);
    const value = useMemo(() => ({
        identity,
        setIdentity,
        identityRoles,
        token,
        setToken,
    }), [identity, identityRoles, token]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
});
export const useAuth = () => {
    const value = useContext(AuthContext);
    if (!value) {
        throw new Error('`useAuth` is used outside of `AuthProvider`');
    }
    return value;
};
